import { useEffect, useRef } from "react";

import type { RefObject } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
].join(",");

/**
 * Elements inside the popup that Tab should visit, in DOM order. `tabIndex >= 0`
 * drops what the browser also skips: MUI's hidden native `<input>` behind a
 * Select, and the unchecked radios of a RadioGroup (a group is one tab stop —
 * arrow keys move within it). `:disabled` (rather than the `disabled` attribute)
 * also catches controls disabled implicitly via an ancestor `<fieldset disabled>`,
 * which stay focus-ineligible even though their own `tabIndex` still reads 0.
 */
const getTabbableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => !element.matches(":disabled") && element.tabIndex >= 0);

/**
 * Cycles focus within the popup instead of letting AG Grid read Tab as grid
 * navigation, which would move to the next cell and discard the open editor.
 * The popup renders under `popupParent` (document.body), so browser-default Tab
 * would walk out of it as well — hence an explicit wrap-around rather than just
 * suppressing the event.
 */
const trapTab = (container: HTMLElement, event: KeyboardEvent): void => {
  const tabbable = getTabbableElements(container);
  if (tabbable.length === 0) return;

  event.preventDefault();
  event.stopPropagation();

  const active = document.activeElement;
  const currentIndex =
    active instanceof HTMLElement ? tabbable.indexOf(active) : -1;

  if (currentIndex === -1) {
    tabbable[event.shiftKey ? tabbable.length - 1 : 0].focus();
    return;
  }

  const step = event.shiftKey ? -1 : 1;
  tabbable[(currentIndex + step + tabbable.length) % tabbable.length].focus();
};

/**
 * Wires Enter-to-save / Escape-to-cancel and Tab containment for a popup cell
 * editor. Registers on `document` in the capture phase, since AG Grid's popup
 * wrapper stops propagation on keydown before it would otherwise bubble from
 * portaled content (e.g. an open MUI Select menu) up to a listener on the
 * editor's own container. Capture also lands ahead of the wrapper's own keydown
 * listener, which is what lets Tab be withheld from the grid.
 */
export function usePopupEditorKeyboard(
  containerRef: RefObject<HTMLElement | null>,
  onSave: () => void,
  onCancel: () => void,
): void {
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  // Containment only engages once focus is inside the popup. Editors that
  // already focus a specific field on mount keep it; the rest open with focus
  // still on the grid cell, where the first Tab would move to the next cell.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const active = document.activeElement;
    if (active instanceof HTMLElement && container.contains(active)) return;

    getTabbableElements(container)[0]?.focus();
  }, [containerRef]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      const container = containerRef.current;
      if (!container) return;
      if (!(event.target instanceof HTMLElement)) return;
      if (!container.contains(event.target)) return;

      if (event.key === "Tab") {
        trapTab(container, event);
      } else if (event.key === "Enter") {
        // A contentEditable surface (the rich-text custom-text editor) owns
        // Enter itself — new paragraph, new list item — so let it through
        // instead of hijacking it to save, same as Carrier Notes' editor.
        if (event.target.isContentEditable) return;
        event.preventDefault();
        onSaveRef.current();
      } else if (event.key === "Escape") {
        onCancelRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [containerRef]);
}
